// @ts-check

import { computed, toValue } from "vue";
import { toJSON, toNumber, trimToUndefined } from "#ce/utils/props";

/**
 * Parses a snap-list source (an array or, from an attribute, a JSON array
 * string like `"[5,10,25]"`) into a sorted, deduplicated list of numbers and
 * exposes nearest-entry lookups, for controls that jump through discrete
 * stops instead of stepping through a continuous range.
 *
 * @param {import("vue").MaybeRefOrGetter<*>} values - the snap-list source (prop ref/getter)
 */
export function useSnapValues(values) {
  const snapValues = computed(() => {
    const parsed = toJSON(trimToUndefined(toValue(values)), true);

    if (!Array.isArray(parsed)) {
      return [];
    }

    const numbers = parsed.map((value) => toNumber(value)).filter((value) => value !== undefined);
    return [...new Set(numbers)].sort((a, b) => a - b);
  });

  const hasSnapValues = computed(() => snapValues.value.length > 0);

  function nearestSnapIndex(value) {
    if (!hasSnapValues.value) {
      return undefined;
    }

    let nearest = 0;
    snapValues.value.forEach((candidate, index) => {
      if (Math.abs(candidate - value) < Math.abs(snapValues.value[nearest] - value)) {
        nearest = index;
      }
    });
    return nearest;
  }

  function nearestSnapValue(value) {
    return snapValues.value[nearestSnapIndex(value)];
  }

  /**
   * The list entry `direction` (+1/-1) steps away from the entry nearest to
   * `value`, or undefined when the step would leave the list.
   */
  function adjacentSnapValue(value, direction) {
    return snapValues.value[nearestSnapIndex(value) + direction];
  }

  return { snapValues, hasSnapValues, nearestSnapIndex, nearestSnapValue, adjacentSnapValue };
}
